import { pagesPath } from "faqSrc/lib/$path";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import React, { VFC } from "react";
import styled, { css } from "styled-components";
import Image from "components/atoms/Image";
import IconClock from "~/components/icons/IconClock";
import { breakPoint } from "~/lib/constants";
import { formatUTCDate } from "~/lib/utils/dateHelper";
import { convertToLinkHref, externalLinkAttrs } from "~/lib/utils/urlHelper";
import { QuestionAnswer } from "~/models/questionAnswer";
import { User } from "~/models/user";

interface FullViewSubQaInfoProps {
  isForceSP?: boolean;
  host?: string;
  updatedAt: QuestionAnswer["updatedAt"];
  user: User | undefined;
}

const FullViewSubQaInfo: VFC<FullViewSubQaInfoProps> = ({
  isForceSP,
  host,
  updatedAt,
  user,
}) => {
  const { t } = useTranslation();
  const userUrl = user
    ? convertToLinkHref({
        host,
        url: pagesPath.creators._id(user.id).$url(),
      })
    : undefined;
  return (
    <Container>
      {user && userUrl && (
        <UserContainer>
          <Link passHref href={userUrl}>
            <UserLink {...externalLinkAttrs(userUrl)} aria-label={user.name}>
              <ImageWrap>
                {user.imageUrl && (
                  <Image
                    alt={user.name}
                    ampLayout="fixed"
                    height={32}
                    layout="fixed"
                    src={user.imageUrl}
                    width={32}
                  />
                )}
              </ImageWrap>
            </UserLink>
          </Link>
          <Link passHref href={userUrl}>
            <UserLink {...externalLinkAttrs(userUrl)}>
              <UsernameWrapper className="hide-scrollbar">
                <JobTitle>
                  {user.jobTitle ? user.jobTitle : "ゲームライター"}
                </JobTitle>
                <div>{user.name}</div>
              </UsernameWrapper>
            </UserLink>
          </Link>
        </UserContainer>
      )}
      <TimestampContainer>
        <IconClock size={16} />
        <TimeText isForceSP={isForceSP}>
          <TimeLabel isForceSP={isForceSP}>
            {t("common:update-text")}:&nbsp;
          </TimeLabel>
          <Time dateTime={formatUTCDate(updatedAt, "ISO")}>
            {formatUTCDate(updatedAt, "Y/M/D H:M")}
          </Time>
        </TimeText>
      </TimestampContainer>
    </Container>
  );
};

const Container = styled.div`
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
  justify-content: space-between;
`;

const UserContainer = styled.div`
  align-items: center;
  display: grid;
  gap: 8px;
  grid-auto-flow: column;
  overflow: hidden;
`;

const UserLink = styled.a`
  &:hover,
  &:active {
    text-decoration: underline var(--title-clr);
  }
`;

const ImageWrap = styled.div`
  align-items: center;
  border-radius: 50%;
  display: grid;
  overflow: hidden;
`;

const UsernameWrapper = styled.div`
  color: var(--title-clr);
  display: grid;
  font-weight: 400;
  gap: 8px;
  grid-auto-flow: column;
  font-size: 16px;
  overflow-x: scroll;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const JobTitle = styled.div`
  font-weight: 700;
`;

const TimestampContainer = styled.div`
  align-items: center;
  color: var(--cap-clr);
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
`;

const TimeText = styled.div<{ isForceSP?: boolean }>`
  font-size: 16px;
  line-height: 1;

  @media only screen and (max-width: ${breakPoint["sm"]}) {
    font-size: 14px;
  }

  ${({ isForceSP }) => {
    if (isForceSP) {
      return css`
        @media only screen and (min-width: 769px) {
          font-size: 14px;
        }
      `;
    }
  }};
`;

const Time = styled.time``;

const TimeLabel = styled.span<{ isForceSP?: boolean }>`
  @media only screen and (max-width: ${breakPoint["sm"]}) {
    display: none;
  }

  ${({ isForceSP }) => {
    if (isForceSP) {
      return css`
        @media only screen and (min-width: 769px) {
          display: none;
        }
      `;
    }
  }};
`;

export default FullViewSubQaInfo;
